import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';

import type { EsnSpatialHierarchyNode } from '../../../shared/types/domain';
import type { EsnSpatialNodeType } from '../../../shared/types/molecules';
import { ButtonComponent } from '../../atoms/button/button.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { SpatialNodeComponent } from '../../molecules/spatial-node/spatial-node.component';

/**
 * Board geometry, straight from the Figma default view (1052×583 board, 220/150px nodes).
 */
const NODE_H = 62;
const WIDE = 220;
const NARROW = 150;
const FLOOR_GAP = 20;
const ROOT_TOP = 23;
const BUS_DROP = 45;
const FLOOR_DROP = 90;
const DEVICE_DROP = 34;
const DEVICE_STEP = 70;
const SIDE = 54;
const MIN_CANVAS = 1048;
const BOTTOM = 40;

const ZOOM_STEP = 0.1;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 2;
const PAN_KEY_STEP = 40;
const DRAG_SLOP = 3;

/**
 * One card placed on the board.
 */
interface PlacedNode {
  node: EsnSpatialHierarchyNode;
  type: EsnSpatialNodeType;
  x: number;
  y: number;
}

/**
 * The island's deployment map: the root space, its floors, and the devices of every open floor,
 * joined by connector lines. The board is a free canvas — zoom and drag move the tree inside a
 * fixed frame. Project an `esn-version-tabs` to pick which map version the board describes.
 */
@Component({
  selector: 'esn-spatial-hierarchy',
  imports: [ButtonComponent, IconComponent, SpatialNodeComponent],
  templateUrl: './spatial-hierarchy.component.html',
  styleUrl: './spatial-hierarchy.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpatialHierarchyComponent {
  /**
   * Root space; its children are floors, theirs are devices.
   */
  readonly root = input.required<EsnSpatialHierarchyNode>();

  readonly heading = input('Spatial Hierarchy');
  readonly subheading = input<string | null>(null);

  readonly collapseAllLabel = input('Collapse all');
  readonly expandAllLabel = input('Expand all');
  readonly zoomInLabel = input('Zoom in');
  readonly zoomOutLabel = input('Zoom out');

  /**
   * Accessible name of the board.
   */
  readonly boardLabel = input('Deployment map');

  /**
   * Ids of the floors whose devices are shown.
   */
  readonly expanded = model<readonly string[]>([]);

  /**
   * Board scale, 0.5–2 (1 = 100%).
   */
  readonly zoom = model(1);

  /**
   * A node card was clicked (not dragged).
   */
  readonly nodeActivated = output<EsnSpatialHierarchyNode>();

  protected readonly pan = signal({ x: 0, y: 0 });
  protected readonly dragging = signal(false);

  private suppressClick = false;
  private stopDrag: (() => void) | null = null;

  private readonly board = viewChild.required<ElementRef<HTMLElement>>('board');

  constructor() {
    // Capture phase, so the click that ends a drag never reaches a card or its chevron.
    const swallow = (event: MouseEvent) => this.swallowDragClick(event);
    let board: HTMLElement | undefined;
    afterNextRender(() => {
      board = this.board().nativeElement;
      board.addEventListener('click', swallow, true);
    });
    inject(DestroyRef).onDestroy(() => {
      this.stopDrag?.();
      board?.removeEventListener('click', swallow, true);
    });
  }

  protected readonly zoomPercent = computed(() => `${Math.round(this.zoom() * 100)}%`);
  protected readonly canZoomIn = computed(() => this.zoom() < ZOOM_MAX - 1e-6);
  protected readonly canZoomOut = computed(() => this.zoom() > ZOOM_MIN + 1e-6);
  protected readonly anyExpanded = computed(() => this.expanded().length > 0);

  protected readonly layout = computed(() => {
    const root = this.root();
    const open = new Set(this.expanded());
    const floors = root.children ?? [];
    const floorsWidth = floors.length * WIDE + Math.max(0, floors.length - 1) * FLOOR_GAP;
    const width = Math.max(MIN_CANVAS, floorsWidth + 2 * SIDE);
    const centre = width / 2;

    const rootBottom = ROOT_TOP + NODE_H;
    const bus = rootBottom + BUS_DROP;
    const floorTop = rootBottom + FLOOR_DROP;
    const floorBottom = floorTop + NODE_H;

    const nodes: PlacedNode[] = [{ node: root, type: 'root', x: centre - WIDE / 2, y: ROOT_TOP }];
    const paths: string[] = [];
    let bottom = rootBottom;

    floors.forEach((floor, i) => {
      const x = centre - floorsWidth / 2 + i * (WIDE + FLOOR_GAP);
      const cx = x + WIDE / 2;
      nodes.push({ node: floor, type: 'floor', x, y: floorTop });
      paths.push(`M${centre} ${rootBottom}V${bus}H${cx}V${floorTop}`);
      bottom = Math.max(bottom, floorBottom);

      const devices = open.has(floor.id) ? (floor.children ?? []) : [];
      devices.forEach((device, j) => {
        const y = floorBottom + DEVICE_DROP + j * DEVICE_STEP;
        nodes.push({ node: device, type: device.placeholder ? 'placeholder' : 'device', x: x + WIDE - NARROW, y });
        bottom = Math.max(bottom, y + NODE_H);
      });

      // One drop from the floor into its first device; the stack below reads as a column.
      if (devices.length) paths.push(`M${cx} ${floorBottom}V${floorBottom + DEVICE_DROP + 20}`);
    });

    return { width, height: bottom + BOTTOM, nodes, paths };
  });

  protected readonly canvasTransform = computed(() => {
    const { x, y } = this.pan();
    return `translate(${x}px, ${y}px) scale(${this.zoom()})`;
  });

  protected isOpen(id: string): boolean {
    return this.expanded().includes(id);
  }

  protected setOpen(id: string, open: boolean): void {
    const rest = this.expanded().filter((e) => e !== id);
    this.expanded.set(open ? [...rest, id] : rest);
  }

  protected toggleAll(): void {
    this.expanded.set(this.anyExpanded() ? [] : (this.root().children ?? []).map((f) => f.id));
  }

  protected zoomBy(delta: number): void {
    const next = Math.round((this.zoom() + delta) * 10) / 10;
    this.zoom.set(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next)));
  }

  protected zoomIn(): void {
    this.zoomBy(ZOOM_STEP);
  }

  protected zoomOut(): void {
    this.zoomBy(-ZOOM_STEP);
  }

  protected activate(node: EsnSpatialHierarchyNode, event: MouseEvent): void {
    // The floor's own chevron toggles it; that is not an activation of the node.
    if ((event.target as HTMLElement).closest('button')) return;
    this.nodeActivated.emit(node);
  }

  /**
   * Swallow the click that ends a drag, so dragging across a card never toggles or opens it.
   */
  private swallowDragClick(event: MouseEvent): void {
    if (!this.suppressClick) return;
    event.stopPropagation();
    event.preventDefault();
    this.suppressClick = false;
  }

  protected onPointerDown(event: PointerEvent): void {
    if (event.button !== 0 || (event.target as HTMLElement).closest('.esn-hierarchy-zoom')) return;
    const start = { x: event.clientX, y: event.clientY, pan: this.pan() };
    let moved = false;

    const move = (e: PointerEvent) => {
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (!moved && Math.hypot(dx, dy) < DRAG_SLOP) return;
      moved = true;
      this.dragging.set(true);
      this.pan.set({ x: start.pan.x + dx, y: start.pan.y + dy });
    };
    const up = () => {
      this.stopDrag?.();
      this.dragging.set(false);
      this.suppressClick = moved;
    };
    this.stopDrag?.();
    this.stopDrag = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      this.stopDrag = null;
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }

  /**
   * Keyboard on the focused board: arrows pan, + and − zoom, 0 resets the view.
   */
  protected onBoardKeydown(event: KeyboardEvent): void {
    if (event.target !== event.currentTarget) return;
    const { x, y } = this.pan();
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [PAN_KEY_STEP, 0],
      ArrowRight: [-PAN_KEY_STEP, 0],
      ArrowUp: [0, PAN_KEY_STEP],
      ArrowDown: [0, -PAN_KEY_STEP],
    };
    if (moves[event.key]) this.pan.set({ x: x + moves[event.key][0], y: y + moves[event.key][1] });
    else if (event.key === '+' || event.key === '=') this.zoomIn();
    else if (event.key === '-') this.zoomOut();
    else if (event.key === '0') {
      this.pan.set({ x: 0, y: 0 });
      this.zoom.set(1);
    } else return;
    event.preventDefault();
  }
}
